import React from 'react'
import { CFormSelect } from '@coreui/react-pro'

interface FilterSelectProps {
  values: string[]
  onChange: (filterFn: (item: string) => boolean) => void
  /**
   * The column's current filter state, handed to a custom filter by CSmartTable as its third
   * argument. It is a string while the table's `columnFilterValue` seed is in force, and becomes
   * the predicate supplied below once the user picks — so it only ever tracks the seed.
   */
  filterState?: unknown
  /** Label for the empty option, which clears the filter. */
  allLabel: string
}

/**
 * Single-select column filter, for columns whose values are a short closed set. CMultiSelect is the
 * wrong shape there: it spends a row on a chip to say one word, and offers a clear and a caret for a
 * choice that is never plural.
 */
const FilterSelect: React.FC<FilterSelectProps> = ({ values, onChange, filterState, allLabel }) => {
  // Derived rather than initialised, because the seed arrives on the render after mount, once
  // CSmartTable has applied columnFilterValue to its own state. Once the user picks, their choice
  // holds - filterState is the predicate below by then, and no longer names a value.
  const [picked, setPicked] = React.useState<string | null>(null)
  const selected = picked ?? (typeof filterState === 'string' ? filterState : '')

  // Union with the seed so a seeded value that no row currently carries still shows as chosen
  // rather than leaving the select blank.
  const options = [...new Set(selected ? [...values, selected] : values)].sort()

  return (
    <CFormSelect
      size="sm"
      value={selected}
      onChange={(e) => {
        const value = e.target.value
        setPicked(value)
        onChange((item) => (value ? item.toLowerCase() === value.toLowerCase() : true))
      }}
    >
      <option value="">{allLabel}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </CFormSelect>
  )
}

export default FilterSelect
